import {
  type ArtistResponse,
  formatArtists,
  pickI18n,
  type PlaylistDTO,
  type PlaylistResponse,
  type SongDTO,
} from '@akpl/shared';
import {
  Button,
  colorFor,
  CoverImage,
  cx,
  doesFocusableExist,
  FocusContext,
  IconArrowLeft,
  IconNote,
  setFocus,
  useFocusable,
} from '@akpl/ui-tv';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { api } from '../api';
import { useRouter } from '../router';
import { useSongActions } from '../song/songActions';
import { useT } from '../useT';
import { listIsFocusable, PLAYLIST_LIST } from './listFocus';
import styles from './Playlist.module.css';
import screens from './screens.module.css';
import type { ScreenProps } from './types';
import { useScreenFocus } from './useScreenFocus';

/** Figma TV-03: back bar at y = 0, header at y = 100, 96 px rows 8 px apart from y = 388. */
export const ROW_HEIGHT = 96;
export const ROW_GAP = 8;
export const LIST_TOP = 388;
export const LIST_VIEWPORT = 616;
/** `limit` per page (docs/03 §2 caps it at 200); a new page is requested this far from the end. */
export const PAGE_SIZE = 100;
const PREFETCH_MARGIN = 12;
const STRIDE = ROW_HEIGHT + ROW_GAP;
const OVERSCAN = 2;

/**
 * Vertical lists scroll only when the focused row would leave the viewport (Figma TV-03 keeps
 * the six rows still while the focus walks down them), unlike the horizontal rows, which pin
 * the focused card to the leading edge.
 */
export function scrollFor(index: number, offset: number, count: number): number {
  const max = Math.max(0, count * STRIDE - ROW_GAP - LIST_VIEWPORT);
  const top = index * STRIDE;
  const bottom = top + ROW_HEIGHT;
  const next =
    top < offset ? top : bottom > offset + LIST_VIEWPORT ? bottom - LIST_VIEWPORT : offset;
  return Math.max(0, Math.min(next, max));
}

export const PLAYLIST_FOCUS = 'playlist-row-0';
export const PLAYLIST_BACK_FOCUS = 'playlist-back';

/**
 * Where a playlist card opens (Home rows, Home heroes, the Search "Listas" tab).
 *
 * An artist-kind playlist is *presented* by `GET /artists/:id`, and that route expects the
 * **artist** id: opening it with the playlist id answers `404 not_found` and the screen dead-
 * ends on "No pudimos cargar esta lista" (QA TV-20 — the seeded Home row `pl_artist_placeholder_1`
 * did exactly that). A row that claims `kind: 'artist'` without an `artistId` is not openable as
 * an artist at all, so it falls back to the plain playlist route instead of 404-ing.
 */
export function playlistTarget(playlist: Pick<PlaylistDTO, 'id' | 'kind' | 'artistId'>): {
  id: string;
  kind: 'playlist' | 'artist';
} {
  return playlist.kind === 'artist' && playlist.artistId
    ? { id: playlist.artistId, kind: 'artist' }
    : { id: playlist.id, kind: 'playlist' };
}

type PageResponse = PlaylistResponse | ArtistResponse;

interface SongRowProps {
  index: number;
  song: SongDTO;
  subtitle: string;
  cue: string;
  onSelect: () => void;
  onFocus: () => void;
}

function SongRow({ index, song, subtitle, cue, onSelect, onFocus }: SongRowProps) {
  const {
    ref,
    focused,
    focusKey: key,
  } = useFocusable<HTMLDivElement>({
    focusKey: `playlist-row-${index}`,
    onEnterPress: onSelect,
    onFocus,
  });
  return (
    <div
      ref={ref}
      role="button"
      data-focus-key={key}
      data-focused={focused || undefined}
      data-testid={`playlist-row-${index}`}
      className={cx(styles.row, focused && styles.rowFocused)}
      style={{ top: index * (ROW_HEIGHT + ROW_GAP) }}
    >
      <span className={styles.index}>{index + 1}</span>
      <span className={styles.cover} style={{ background: colorFor(song.id) }}>
        <CoverImage src={song.imageUrl} width={72} height={72} fallback={<IconNote size={36} />} />
      </span>
      <span className={styles.info}>
        <span className={styles.title}>{song.title}</span>
        {subtitle ? <span className={styles.artists}>{subtitle}</span> : null}
      </span>
      {focused ? <span className={styles.cue}>{cue}</span> : null}
    </div>
  );
}

/**
 * Playlist view (Figma TV-03) — also the artist view: `GET /artists/:id` answers with the
 * same shape plus `artist`, so one screen serves both (docs/03 §2 "Catalog"). It is one level
 * deeper than Home, so it carries a focusable "← Atrás" pill and a breadcrumb (docs/04 §7.4),
 * and it renders no durations at all (§7.3). Enter on a row opens the song action dialog.
 */
export default function Playlist({ params }: ScreenProps<'playlist'>) {
  const { t, i18n } = useT();
  const locale = i18n.language === 'en' ? 'en' : 'es';
  const { id, kind } = params;
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [offset, setOffset] = useState(0);
  const { openSongActions, overlay } = useSongActions();
  const back = useRouter((s) => s.back);
  const reset = useRouter((s) => s.reset);

  const query = useInfiniteQuery({
    queryKey: ['playlist', kind, id],
    initialPageParam: 0,
    queryFn: ({ pageParam }): Promise<PageResponse> => {
      const args = { params: { id }, query: { offset: pageParam, limit: PAGE_SIZE } };
      return kind === 'artist' ? api.artist(args) : api.playlist(args);
    },
    getNextPageParam: (last: PageResponse, pages: PageResponse[]) => {
      const loaded = pages.reduce((n, page) => n + page.songs.length, 0);
      return loaded > 0 && loaded < last.total ? loaded : undefined;
    },
  });

  const first = query.data?.pages[0];
  const songs = useMemo(
    () => (query.data?.pages ?? []).flatMap((page) => page.songs),
    [query.data],
  );
  const total = first?.total ?? 0;

  useScreenFocus('playlist', PLAYLIST_FOCUS, songs.length > 0);

  // A list that answers 404 (or comes back empty) used to leave the remote with nothing to
  // aim at: the opener card is unmounted, so no arrow key did anything and only Back worked.
  // The "← Atrás" pill takes the focus instead (QA TV-20).
  const dead = songs.length === 0 && !query.isPending;
  useEffect(() => {
    if (!dead || !doesFocusableExist(PLAYLIST_BACK_FOCUS)) return;
    void setFocus(PLAYLIST_BACK_FOCUS);
  }, [dead]);

  // Offset/limit paging driven by the focus, not by a scrollbar (docs/04 §2).
  useEffect(() => {
    if (!query.hasNextPage || query.isFetchingNextPage) return;
    if (focusedIndex >= songs.length - PREFETCH_MARGIN) void query.fetchNextPage();
  }, [focusedIndex, songs.length, query]);

  const onRowFocus = useCallback((index: number, count: number) => {
    setFocusedIndex(index);
    setOffset((current) => scrollFor(index, current, count));
  }, []);

  const start = Math.max(0, Math.floor(offset / STRIDE) - OVERSCAN);
  const end = Math.min(songs.length, Math.ceil((offset + LIST_VIEWPORT) / STRIDE) + OVERSCAN);

  const { ref: listRef, focusKey: listKey } = useFocusable<HTMLDivElement>({
    focusKey: PLAYLIST_LIST.containerKey,
    // UX-045: same rule as Search — a list with no rows is not a focus target, or norigin parks
    // the focus on the bare container and the watchdog repairs on every press.
    focusable: listIsFocusable(songs.length),
    trackChildren: true,
    saveLastFocusedChild: true,
  });

  const name = first ? pickI18n(first.playlist.name, locale) : '';
  const kicker = kind === 'artist' ? t('tv:playlist.artistKicker') : t('tv:playlist.kicker');
  // "Inicio › Listas › Clásicos del rock" (Figma TV-03 66:1663). No durations anywhere.
  const section =
    kind === 'artist' ? t('tv:playlist.crumbArtists') : t('tv:playlist.crumbPlaylists');
  const breadcrumb = [t('tv:rail.home'), section, name].filter(Boolean).join(' › ');
  const cue = t('tv:playlist.cue');

  return (
    <>
      <div className={screens.content} data-testid="screen-playlist">
        <div className={styles.backBar}>
          <Button
            focusKey={PLAYLIST_BACK_FOCUS}
            icon={<IconArrowLeft size={32} />}
            className={styles.backButton}
            data-testid="playlist-back"
            onSelect={() => {
              if (!back()) reset('home');
            }}
          >
            {t('tv:playlist.back')}
          </Button>
          <span className={styles.breadcrumb}>{breadcrumb}</span>
        </div>

        <div className={styles.header}>
          <span className={styles.headerCover} style={{ background: colorFor(id) }}>
            <CoverImage
              src={first?.playlist.imageUrl}
              width={240}
              height={240}
              fallback={<IconNote size={120} />}
            />
          </span>
          <div className={styles.headerText}>
            <div className={styles.kicker}>{kicker}</div>
            <h1 className={styles.name}>{name || t('common:states.loading')}</h1>
            <div className={styles.meta}>
              {first ? t('tv:playlist.songs', { count: total }) : ''}
            </div>
            <div className={styles.hint}>{songs.length > 0 ? t('tv:playlist.hint') : ''}</div>
          </div>
        </div>

        <FocusContext.Provider value={listKey}>
          <div ref={listRef} className={styles.list} data-testid="playlist-list">
            {query.isError ? (
              <div className={screens.empty} role="alert">
                {t('tv:playlist.loadError')}
              </div>
            ) : songs.length === 0 ? (
              <div className={screens.empty}>
                {query.isPending ? t('common:states.loading') : t('tv:playlist.empty')}
              </div>
            ) : (
              <div className={styles.track} style={{ transform: `translateY(${-offset}px)` }}>
                {songs.slice(start, end).map((song, slot) => {
                  const index = start + slot;
                  return (
                    <SongRow
                      key={`${song.id}-${index}`}
                      index={index}
                      song={song}
                      subtitle={formatArtists(song.artists, locale)}
                      cue={cue}
                      onFocus={() => onRowFocus(index, songs.length)}
                      onSelect={() => openSongActions(song)}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </FocusContext.Provider>
      </div>
      {overlay}
    </>
  );
}
